import { useEffect, useState } from 'react';
import { useParams, Outlet, useNavigate } from 'react-router-dom';
import authClient from './authClient';
import { ForbiddenPage } from '../pages/ForbiddenPage';

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

export function OrgGuard() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { data: organizations, isPending: orgsLoading } = authClient.useListOrganizations();
  const { data: activeOrganization, isPending: activeLoading } = authClient.useActiveOrganization();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(false);

  useEffect(() => {
    if (orgsLoading || activeLoading || !orgSlug) return;

    const targetOrg = organizations?.find(org => org.slug === orgSlug);

    if (!targetOrg) {
      // User doesn't have access to this org - will show forbidden page
      return;
    }

    // If active org doesn't match URL, sync it
    if (activeOrganization?.slug !== orgSlug) {
      setIsSyncing(true);
      setSyncError(false);
      authClient.organization.setActive({ organizationSlug: orgSlug })
        .then(() => {
          setIsSyncing(false);
        })
        .catch((err) => {
          console.error('Failed to set active org:', err);
          setIsSyncing(false);
          setSyncError(true);
        });
    }
  }, [orgSlug, organizations, activeOrganization, orgsLoading, activeLoading, navigate]);

  // Show loading while we're figuring things out
  if (orgsLoading || activeLoading || isSyncing) {
    return <LoadingSpinner />;
  }

  // Verify the org exists in user's list
  const targetOrg = organizations?.find(org => org.slug === orgSlug);
  if (!targetOrg || syncError) {
    return <ForbiddenPage orgSlug={orgSlug} />;
  }

  // Ready to render org-scoped content
  return <Outlet />;
}
